// frontend/shared-ui/src/components/LogoUploader.jsx
import React, { useRef, useState } from "react";
import Button from "./Button";
import Spinner from "./Spinner";

export default function LogoUploader({
  value,
  onChange,
  upload,
  resolveUrl,
  label = "Logo",
  hint = "Square PNG or SVG works best · max 5 MB",
  accept = "image/*",
  maxSizeMB = 5,
  size = 72,
  disabled = false,
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [dragging, setDragging] = useState(false);

  const displayUrl = value
    ? typeof resolveUrl === "function"
      ? resolveUrl(value)
      : value
    : "";

  const doUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please upload an image file");
      return;
    }
    if (maxSizeMB && file.size > maxSizeMB * 1024 * 1024) {
      setError(`Image too large — max ${maxSizeMB} MB`);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const result = await upload(file);
      const url = result?.url || result?.file?.url || result;
      if (typeof url !== "string") throw new Error("Upload returned no URL");
      onChange?.(url);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (busy || disabled) return;
    const file = e.dataTransfer.files?.[0];
    if (file) doUpload(file);
  };

  const remove = () => {
    if (busy) return;
    onChange?.("");
  };

  return (
    <div className="fh-logo-uploader">
      <label className="fh-form-field__label">{label}</label>

      <div className="fh-logo-uploader__row">
        <div
          className={`fh-logo-uploader__preview ${dragging ? "is-drag" : ""}`}
          style={{ width: size, height: size }}
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled) setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => !disabled && !busy && inputRef.current?.click()}
          role="button"
          tabIndex={0}
          title="Click or drag an image to upload"
        >
          {displayUrl ? (
            <img
              src={displayUrl}
              alt="Logo"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          ) : (
            <div className="fh-logo-uploader__placeholder">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <path d="M21 15l-5-5L5 21" />
              </svg>
            </div>
          )}
          {busy && (
            <div className="fh-logo-uploader__busy">
              <Spinner size="md" />
            </div>
          )}
        </div>

        <div className="fh-logo-uploader__actions">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => inputRef.current?.click()}
            disabled={disabled || busy}
          >
            {value ? "Replace" : "Upload"}
          </Button>
          {value && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={remove}
              disabled={disabled || busy}
            >
              Remove
            </Button>
          )}
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: "none" }}
        onChange={(e) => doUpload(e.target.files?.[0])}
        disabled={disabled}
      />

      {error && <div className="fh-form-field__error">{error}</div>}
      {hint && !error && <div className="fh-form-field__hint">{hint}</div>}
    </div>
  );
}
